import React from 'react';
import { AlertTriangle, Clock, X, Trash2, Flag, CheckCheck } from 'lucide-react';
import { useNotifications, REMINDER_OFFSETS } from '../../context/NotificationContext';
import './RightSidebar.css';

function timeAgo(isoString) {
  const now = new Date();
  const then = new Date(isoString);
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  return `${diffDay}d ago`;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function NotificationCard({ notification, onDismiss, onRemove }) {
  const isDeadline = notification.type === 'deadline';
  const offsetLabel = REMINDER_OFFSETS[notification.reminderOffset]?.label || '';
  const priorityClass = notification.priority
    ? `notif-card__priority notif-card__priority--${notification.priority.toLowerCase()}`
    : '';
  const daysLabel = notification.daysUntil === 1 ? '1 day left' : `${notification.daysUntil} days left`;

  return (
    <div className={`notif-card ${isDeadline ? 'notif-card--deadline' : ''} ${notification.read ? 'notif-card--read' : ''}`}>
      <div className="notif-card__icon-wrapper">
        {isDeadline ? (
          <AlertTriangle size={16} strokeWidth={2} />
        ) : (
          <Clock size={16} strokeWidth={2} />
        )}
      </div>

      <div className="notif-card__content">
        <div className="notif-card__header-row">
          <span className="notif-card__task-text">
            {isDeadline ? 'Application deadline soon' : notification.taskText}
          </span>
          <button
            className="notif-card__remove"
            onClick={() => onRemove(notification.id)}
            aria-label="Remove notification"
          >
            <X size={12} strokeWidth={2} />
          </button>
        </div>

        <span className="notif-card__college">{notification.collegeName}</span>

        <div className="notif-card__meta">
          {isDeadline ? (
            <>
              <span className="notif-card__deadline-badge">{daysLabel}</span>
              <span className="notif-card__offset">Due {formatDate(notification.deadlineDate)}</span>
            </>
          ) : notification.priority && (
            <span className={priorityClass}>
              <Flag size={9} />
              {notification.priority}
            </span>
          )}
          {!isDeadline && <span className="notif-card__offset">{offsetLabel}</span>}
          <span className="notif-card__time">{timeAgo(notification.firedAt)}</span>
        </div>

        {!notification.read && (
          <button
            className="notif-card__mark-read"
            onClick={() => onDismiss(notification.id)}
          >
            Mark as read
          </button>
        )}
      </div>
    </div>
  );
}

function RightSidebar({ isOpen }) {
  const {
    notifications,
    unreadCount,
    dismissNotification,
    removeNotification,
    clearAll,
    markAllRead,
  } = useNotifications();

  return (
    <div className={`right-sidebar ${isOpen ? 'right-sidebar--open' : ''}`}>
      {/* ── Header ── */}
      <div className="right-sidebar__header">
        <div className="right-sidebar__title-row">
          <h2 className="right-sidebar__title">Notifications</h2>
          {unreadCount > 0 && (
            <span className="right-sidebar__unread-badge">{unreadCount}</span>
          )}
        </div>

        {notifications.length > 0 && (
          <div className="right-sidebar__actions">
            {unreadCount > 0 && (
              <button className="right-sidebar__action-btn" onClick={markAllRead}>
                <CheckCheck size={13} />
                Read all
              </button>
            )}
            <button className="right-sidebar__action-btn right-sidebar__action-btn--danger" onClick={clearAll}>
              <Trash2 size={13} />
              Clear
            </button>
          </div>
        )}
      </div>

      {/* ── Notification List ── */}
      {notifications.length === 0 ? (
        <div className="right-sidebar__empty">
          <div className="right-sidebar__empty-icon">
            <Clock size={28} strokeWidth={1.5} />
          </div>
          <p className="right-sidebar__empty-title">No notifications</p>
          <p className="right-sidebar__empty-text">
            Set task reminders and college deadlines to see them here
          </p>
        </div>
      ) : (
        <div className="right-sidebar__list">
          {notifications.map((notification) => (
            <NotificationCard
              key={notification.id}
              notification={notification}
              onDismiss={dismissNotification}
              onRemove={removeNotification}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default RightSidebar;
